export default class DropdownMenu {
  /**
   * @param select {HTMLInputElement}
   */
  constructor({ navigation }) {
    this.dom = {
      navigation,
      mainNavLinks: [...navigation.querySelectorAll('.main-nav-link[data-dropdown-target]')],
      navLinks: [...navigation.querySelectorAll('.nav-link[data-dropdown-target]')],
      dropdownMenus: [...navigation.querySelectorAll('.l-dropdown-menu:not(.offcanvas)')],
      dropdownCategories: [...navigation.querySelectorAll('.dropdown-category')],
      dropdownColumns: [...navigation.querySelectorAll('.dropdown-column > .offcanvas')],
      currentNavItem: null,
    };

    this.data = {
      options: {
        breakpoint: 768,
        wait: 400,
      },
      timerFn: null,
      hoverDelayTimerFn: null,
    };

    this.properties = {
      mq: window.matchMedia(`(min-width: ${this.data.options.breakpoint}px)`),
    };

    this.events = {
      onNavLinkMouseEnter: this.onNavLinkMouseEnter.bind(this),
      onNavLinkMouseLeave: this.onNavLinkMouseLeave.bind(this),
      onDropdownMenuMouseEnter: this.onDropdownMenuMouseEnter.bind(this),
      onDropdownMenuMouseLeave: this.onDropdownMenuMouseLeave.bind(this),
      onDropdownColumnMouseEnter: this.onDropdownColumnMouseEnter.bind(this),
      onWindowResize: this.onWindowResize.bind(this),
      onMediaQueryChange: this.onMediaQueryChange.bind(this),
    };
  }

  /**
   * @return {this}
   */
  mount() {
    this.bindEvents();

    this.properties.mq.addEventListener('change', this.events.onMediaQueryChange);
    this.setDropdownOffset();

    return this;
  }

  /**
   * Bind / unbind the events
   */
  bindEvents() {
    if (this.properties.mq.matches) {
      // bind events for desktop
      this.dom.navLinks.forEach((link) => {
        link.addEventListener('mouseenter', this.events.onNavLinkMouseEnter);
        link.addEventListener('mouseleave', this.events.onNavLinkMouseLeave);
      });
      this.dom.dropdownMenus.forEach((dropdown) => {
        dropdown.addEventListener('mouseenter', this.events.onDropdownMenuMouseEnter);
        dropdown.addEventListener('mouseleave', this.events.onDropdownMenuMouseLeave);
      });
      this.dom.dropdownColumns.forEach((dropdownColumn) => {
        dropdownColumn.addEventListener('mouseenter', this.events.onDropdownColumnMouseEnter);
      });
      window.addEventListener('resize', this.events.onWindowResize);
      this.resetDropdown();
    } else {
      // Unbind events for mobile
      this.dom.navLinks.forEach((link) => {
        link.removeEventListener('mouseenter', this.events.onNavLinkMouseEnter);
        link.removeEventListener('mouseleave', this.events.onNavLinkMouseLeave);
      });
      this.dom.dropdownMenus.forEach((dropdown) => {
        dropdown.removeEventListener('mouseenter', this.events.onDropdownMenuMouseEnter);
        dropdown.removeEventListener('mouseleave', this.events.onDropdownMenuMouseLeave);
      });
      this.dom.dropdownColumns.forEach((dropdownColumn) => {
        dropdownColumn.removeEventListener('mouseenter', this.events.onDropdownColumnMouseEnter);
      });
      window.removeEventListener('resize', this.events.onWindowResize);
      // Remove all inline css
      [...this.dom.navigation.querySelectorAll('[style]')].forEach((element) => {
        element.removeAttribute('style');
      });
    }
  }

  onNavLinkMouseEnter(e) {
    document.getElementById(e.target.dataset.dropdownTarget).classList.add('open');

    this.dom.currentNavItem = e.target;

    if (!this.data.hoverDelayTimerFn) {
      this.data.hoverDelayTimerFn = setTimeout(() => {
        this.showDropdown();
        this.data.hoverDelayTimerFn = null;
      }, this.data.options.wait);
    }

    if (this.dom.currentNavItem.classList.contains('dropdown-link')) {
      this.clearAllActiveStates();
      this.dom.currentNavItem.classList.add('active');
      this.setDropdownDisplay();
    }
  }

  onNavLinkMouseLeave(e) {
    document.getElementById(e.target.dataset.dropdownTarget).classList.remove('open');

    if (e.target.classList.contains('dropdown-link')) {
      e.target.classList.remove('active');
    }

    this.dom.currentNavItem = null;

    if (e.target.classList.contains('dropdown-link')) {
      this.setDropdownDisplay(e.target);
    }
  }

  onDropdownMenuMouseEnter(e) {
    document.getElementById(e.target.id).classList.add('open');
    if (this.data.timerFn) {
      clearTimeout(this.data.timerFn);
    }
  }

  /**
   * Close the dropdown when the timer ends
   * @param e
   */
  onDropdownMenuMouseLeave(e) {
    this.data.timerFn = setTimeout(() => this.closeDropdown(e), this.data.options.wait);
  }

  // eslint-disable-next-line class-methods-use-this
  onDropdownColumnMouseEnter(e) {
    if (document.querySelector(`[data-dropdown-target='${e.target.id}']`)) {
      document.querySelector(`[data-dropdown-target='${e.target.id}']`).classList.add('active');
    }
  }

  showDropdown() {
    if (!this.dom.currentNavItem) {
      return;
    }

    this.dom.currentNavItem.classList.add('open');
  }

  /**
   * Close the dropdown and resets the timer
   * @param e
   */
  closeDropdown(e) {
    document.getElementById(e.target.id).classList.remove('open');
    this.data.timerFn = null;
    this.resetDropdown();
  }

  /**
   * Select al dropdown elements and reset the z-index to 1
   * The current selected dropdown gets a higher z-index
   */
  setDropdownDisplay(el) {
    Array.prototype.slice.call(this.dom.dropdownColumns).forEach((node) => {
      if (node.classList.contains('main-categories')) {
        node.style.setProperty('display', 'none');
      }
    });

    if (this.dom.currentNavItem) {
      document.getElementById(this.dom.currentNavItem.dataset.dropdownTarget).style.setProperty('display', 'block');
    }
    if (el) {
      document.getElementById(el.dataset.dropdownTarget).style.setProperty('display', 'block');
    }
  }

  /**
   * Set the offset for the dropdowns
   */
  setDropdownOffset() {
    this.dom.mainNavLinks.forEach((link) => {
      document.getElementById(link.dataset.dropdownTarget).style.left = `${link.getBoundingClientRect().x}px`;
    });
  }

  /**
   * Checks when the media query changes and unbind / bind the events
   */
  onMediaQueryChange() {
    this.bindEvents();
  }

  /**
   * Checks if the user resize the window
   */
  // eslint-disable-next-line class-methods-use-this
  onWindowResize() {
    this.setDropdownOffset();
  }

  /**
   * Function that resets the dropdown,
   * Hides all the super categorie columns and show the first one
   * Reset all active states
   */
  resetDropdown() {
    Array.prototype.slice.call(this.dom.dropdownColumns).forEach((node) => {
      if (node.classList.contains('main-categories')) {
        node.style.setProperty('display', 'none');
      }
    });
    Array.prototype.slice.call([...this.dom.navigation.querySelectorAll('.l-dropdown-menu:not(.offcanvas) .main-categories:first-child')]).forEach((node) => {
      node.style.setProperty('display', 'block');
    });

    this.clearAllActiveStates();

    /**
     * Loop through the dropdown columns
     * Check if it's a column with super categories
     * Select the first dropdown link and set the active class
     */
    this.dom.dropdownColumns.forEach((column) => {
      if (column.classList.contains('super-categories')) {
        column.querySelector('.dropdown-link').classList.add('active');
      }
    });
  }

  clearAllActiveStates() {
    /**
     * Remove all active states
     */
    this.dom.navLinks.forEach((link) => {
      if (link.classList.contains('dropdown-link')) {
        link.classList.remove('active');
      }
    });
  }
}
